import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Gem, KeyRound, Lightbulb, Loader2, Sparkles, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CinematicVideo } from "@/components/CinematicVideo";

const TREASURE_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/er0x7o6c_gemini_generated_video_90d81bab.mp4";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function TreasureHunt() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guess, setGuess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hintLevel, setHintLevel] = useState(0);
  const [wrong, setWrong] = useState(false);
  const [solved, setSolved] = useState(null); // { answer, nugget }
  const [imgReady, setImgReady] = useState(false);

  useEffect(() => {
    if (!solved || !solved.date) return;
    const url = `${BACKEND_URL}/api/treasure/image/${solved.date}/${solved.lang}`;
    let stop = false;
    let tries = 0;
    const check = () => {
      if (stop) return;
      const im = new Image();
      im.onload = () => { if (!stop) setImgReady(true); };
      im.onerror = () => { tries += 1; if (!stop && tries < 12) setTimeout(check, 3000); };
      im.src = url + (tries ? `?r=${tries}` : "");
    };
    if (solved.image) setImgReady(true); else { setImgReady(false); check(); }
    return () => { stop = true; };
  }, [solved]); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    setSolved(null); setHintLevel(0); setWrong(false); setGuess(""); setImgReady(false);
    try {
      const { data } = await api.get("/treasure/today", { params: { lang } });
      setData(data);
    } catch { setData(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [lang]); // eslint-disable-line

  const submit = async () => {
    if (!guess.trim() || submitting) return;
    setSubmitting(true); setWrong(false);
    try {
      const { data: res } = await api.post("/treasure/guess", { guess, lang }, { params: { lang } });
      if (res.correct) setSolved({ answer: res.answer, nugget: res.nugget, image: res.image, date: res.date, lang: res.lang });
      else { setWrong(true); setHintLevel((h) => Math.min((data?.hints?.length || 0), h + 1)); }
    } catch { setWrong(true); }
    finally { setSubmitting(false); }
  };

  const reveal = async () => {
    try {
      const { data: res } = await api.get("/treasure/reveal", { params: { lang } });
      setSolved({ answer: res.answer, nugget: res.nugget, image: res.image, date: res.date, lang: res.lang, revealed: true });
    } catch {}
  };

  const share = async () => {
    if (!solved) return;
    const text = `${t("treasure.eyebrow")} — ${t("treasure.answerLabel")} : ${solved.answer}\n\n${solved.nugget}\n\nCOCOON MIND · ${window.location.origin}`;
    if (navigator.share) {
      try { await navigator.share({ title: "COCOON MIND", text }); return; } catch { /* cancelled */ }
    }
    try { await navigator.clipboard.writeText(text); toast.success(t("treasure.shareCopied")); }
    catch { /* ignore */ }
  };

  const hints = data?.hints || [];
  const allHintsUsed = hintLevel >= hints.length && hints.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
      data-testid="treasure-hunt"
      className="max-w-4xl mx-auto"
    >
      <div className="relative rounded-[2rem] overflow-hidden border border-primary/20 frame-pulse glass treasure-glass">
      <div className="relative z-10 px-6 sm:px-12 py-14 text-center overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-[0.5]" style={{ background: "radial-gradient(120% 90% at 50% -10%, hsl(38 60% 60% / 0.08), transparent 60%)" }} />
        {/* subtle inner frame + corner ornaments */}
        <div className="pointer-events-none absolute inset-3 sm:inset-4 rounded-2xl border border-white/[0.06]" />
        <span className="pointer-events-none absolute top-2.5 left-2.5 w-6 h-6 border-t border-l border-white/25 rounded-tl-lg" />
        <span className="pointer-events-none absolute top-2.5 right-2.5 w-6 h-6 border-t border-r border-white/25 rounded-tr-lg" />
        <span className="pointer-events-none absolute bottom-2.5 left-2.5 w-6 h-6 border-b border-l border-white/25 rounded-bl-lg" />
        <span className="pointer-events-none absolute bottom-2.5 right-2.5 w-6 h-6 border-b border-r border-white/25 rounded-br-lg" />
        <div className="relative flex flex-col items-center">
          <span className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
            style={{ background: "radial-gradient(circle at 35% 30%, hsl(44 96% 72%), hsl(28 85% 50%))" }}>
            <Gem size={22} className="text-[hsl(22,60%,16%)]" />
          </span>
          <h2 className="font-display text-gradient-orange text-4xl sm:text-6xl leading-[1.12] pb-[0.08em] mb-3" data-testid="treasure-eyebrow">{t("treasure.eyebrow")}</h2>
          <p className="text-sm text-foreground/50 mb-5 max-w-xl">{t("treasure.prompt")}</p>

          <div className="w-full max-w-2xl mx-auto mb-10">
            <CinematicVideo fade noSound silver src={TREASURE_VIDEO} testId="treasure-cinematic-video" />
          </div>

          {loading ? (
            <p className="font-display italic text-foreground/40 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> {t("treasure.loading")}</p>
          ) : !data ? (
            <p className="font-display italic text-foreground/40">{t("treasure.error")}</p>
          ) : (
            <>
              <p className="font-display italic text-lg sm:text-2xl text-foreground/90 leading-relaxed max-w-2xl" data-testid="treasure-riddle">
                {data.riddle}
              </p>

              {/* revealed hints */}
              <AnimatePresence>
                {hintLevel > 0 && !solved && (
                  <motion.ul initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0 }}
                    className="mt-5 space-y-1.5 text-sm text-foreground/60 w-full max-w-xl">
                    {hints.slice(0, hintLevel).map((h, i) => (
                      <li key={i} className="flex items-start gap-2 justify-center" data-testid={`treasure-hint-${i}`}>
                        <Lightbulb size={13} className="text-primary/70 mt-0.5 flex-shrink-0" /> <span>{h}</span>
                      </li>
                    ))}
                  </motion.ul>
                )}
              </AnimatePresence>

              {!solved ? (
                <>
                  <div className="mt-6 flex flex-col sm:flex-row gap-3 w-full max-w-md">
                    <input
                      data-testid="treasure-input"
                      value={guess}
                      onChange={(e) => { setGuess(e.target.value); setWrong(false); }}
                      onKeyDown={(e) => e.key === "Enter" && submit()}
                      placeholder={t("treasure.placeholder")}
                      className={`flex-1 bg-white/[0.04] border rounded-full px-5 py-3 text-foreground outline-none transition-colors ${wrong ? "border-red-400/60" : "border-primary/25 focus:border-primary/60"}`}
                    />
                    <button data-testid="treasure-submit" onClick={submit} disabled={submitting}
                      className="btn-frost-orange px-6 py-3 rounded-full font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                      {submitting ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />} {t("treasure.submit")}
                    </button>
                  </div>
                  {wrong && <p className="mt-3 text-sm text-red-300/80" data-testid="treasure-wrong">{t("treasure.wrong")}</p>}
                  <div className="mt-4 flex items-center gap-4 text-xs">
                    {!allHintsUsed && (
                      <button data-testid="treasure-hint-btn" onClick={() => setHintLevel((h) => Math.min(hints.length, h + 1))}
                        className="inline-flex items-center gap-1.5 text-foreground/45 hover:text-primary transition-colors">
                        <Lightbulb size={13} /> {t("treasure.hint")}
                      </button>
                    )}
                    {allHintsUsed && (
                      <button data-testid="treasure-reveal-btn" onClick={reveal}
                        className="inline-flex items-center gap-1.5 text-foreground/45 hover:text-primary transition-colors">
                        <KeyRound size={13} /> {t("treasure.reveal")}
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
                  className="mt-7 w-full max-w-xl" data-testid="treasure-solved">
                  <p className="inline-flex items-center gap-2 text-primary font-semibold">
                    <Sparkles size={16} /> {solved.revealed ? t("treasure.revealed") : t("treasure.solved")}
                  </p>
                  {imgReady && (
                    <motion.img
                      data-testid="treasure-image"
                      initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1 }}
                      src={`${BACKEND_URL}/api/treasure/image/${solved.date}/${solved.lang}`}
                      alt={t("treasure.imageAlt")}
                      loading="lazy"
                      className="mt-4 w-full rounded-2xl border border-white/10 shadow-lg object-cover aspect-[4/3]"
                    />
                  )}
                  <p className="mt-4 text-sm text-foreground/50">{t("treasure.answerLabel")} : <span className="text-foreground/80 font-medium">{solved.answer}</span></p>
                  <div className="mt-4 rounded-2xl bg-primary/[0.07] border border-primary/20 px-5 py-4 text-left">
                    <p className="uppercase tracking-[0.2em] text-[10px] text-primary/70 mb-1.5 flex items-center gap-1.5"><Gem size={11} /> {t("treasure.nuggetLabel")}</p>
                    <p className="font-display italic text-foreground/85 leading-relaxed" data-testid="treasure-nugget">{solved.nugget}</p>
                  </div>
                  <button data-testid="treasure-share" onClick={share}
                    className="mt-5 btn-frost-orange px-6 py-2.5 rounded-full font-semibold inline-flex items-center justify-center gap-2 mx-auto">
                    <Share2 size={15} /> {t("treasure.share")}
                  </button>
                  <p className="mt-4 text-xs text-foreground/40">{t("treasure.tomorrow")}</p>
                </motion.div>
              )}
            </>
          )}
        </div>
      </div>
      </div>
    </motion.div>
  );
}
